import React, { useEffect, useState, useRef } from "react"
import "./EmailTemplateTable.scss"
import { useNavigate } from "react-router-dom"
import EmailApi from "../../../Api/emailApi"
import {
  Button,
  Col,
  Row,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination, StatusBadge, ActionIcon } from "components/Common"

const EmailTemplateDataTable = () => {
  document.title = "Email Templates"
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")
  const [emailTemplateDetails, setEmailTemplateDetails] = useState([])
  const [filteredTemplateDetails, setFilteredTemplateDetails] = useState([])
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [filterStatus, setFilterStatus] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)
  const [selectedTemplates, setSelectedTemplates] = useState([])
  const op = useRef(null)
  const userData = JSON.parse(localStorage.getItem("userData"))
  const permissions = userData?.permission || []
  const [deleteModal, setDeleteModal] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState(null)
  const [otpInput, setOtpInput] = useState("")
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deletedReason, setDeletedReason] = useState("")

  const deleteToggle = () => setDeleteModal(!deleteModal)
  const toast = useRef(null)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Email", link: "/EmailTemplateTable" },
    { label: "Email Template List" },
  ]
  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const getAllEmailTemplates = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    EmailApi.getALLEmailTemplates(config)
      .then(response => {
        setLoading(false)
        setEmailTemplateDetails(response.data.data)
        setTotalRecords(response.data.totalTemplate)
      })
      .catch(error => {
        console.error("Error fetching email templates:", error)
      })
  }

  useEffect(() => {
    getAllEmailTemplates()
  }, [accessToken, page, limit])

  useEffect(() => {
    let filteredData = emailTemplateDetails

    // Filter by status
    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        data => data?.verifiedStatus?.toLowerCase() === filterStatus,
      )
    }

    // Filter by search term
    if (searchTerm) {
      filteredData = filteredData.filter(data =>
        data.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        data.subject?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredTemplateDetails(filteredData)
  }, [filterStatus, emailTemplateDetails, searchTerm])

  const totalPages = Math.ceil(totalRecords / limit)

  const handleAddTemplateClick = () => {
    navigate("/addEmailTemplate")
  }

  const toggleSelectAll = () => {
    if (selectedTemplates.length === filteredTemplateDetails.length) {
      setSelectedTemplates([])
    } else {
      setSelectedTemplates(filteredTemplateDetails.map(tpl => tpl._id))
    }
  }

  const toggleSelection = templateId => {
    setSelectedTemplates(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId],
    )
  }

  const handleUpdateStatus = newStatus => {
    if (selectedTemplates.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select at least one template to update.",
        life: 3000,
      })
      // toast.warn("Please select at least one template to update.")
      return
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const payload = {
      emailTemplateIds: selectedTemplates,
      newStatus: newStatus,
    }
    

    EmailApi.updateEmailTemplateStatus(payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response.data.message,
            life: 3000,
          })
          // toast.success(response.data.message)
          getAllEmailTemplates()
          setSelectedTemplates([])
        }
      })
      .catch(error => {
        console.error("Error updating template status:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.message ||
            "Failed to update template status.",
          life: 3000,
        })
        // toast.error("Failed to update template status.")
      })
  }

  const hasPermission = (permissions, resource, method) => {
    if (!Array.isArray(permissions.permission)) {
      console.error("Permissions is not an array:", permissions)
      return false
    }

    return permissions.permission.some(
      perm =>
        perm.permissionName === resource &&
        perm.permissionType.includes(method),
    )
  }

  const handleDeleteTemplate = id => {
    if (!id) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Invalid template ID",
        life: 3000,
      })
      return
    }

    
    setTemplateToDelete(id)
    setDeleteModal(true)
    setOtpInput("")
    setDeletedReason("")
    setIsOtpSent(false)
  }

  
  const generateOtp = () => {
    if (!templateToDelete) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Template ID is missing",
        life: 3000,
      });
      return;
    }
  
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  
    setIsDeleting(true);
  
    EmailApi.generateDeleteTemplateOtp(templateToDelete, config)
      .then(response => {
        if (response.data.status) {
          setIsOtpSent(true);
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response.data.message || "Delete confirmation email sent to admin",
            life: 3000,
          });
        }
      })
      .catch(error => {
        console.error("Error generating OTP:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to send OTP",
          life: 3000,
        });
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };
  
  const handleOtpChange = e => {
    const value = e.target.value;
    if (value.length <= 5) {
      setOtpInput(value);
    }
  };
  
  const confirmDelete = () => {
    if (!templateToDelete) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Template ID is missing",
        life: 3000,
      });
      return;
    }
  
    if (!otpInput) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter OTP",
        life: 3000,
      });
      return;
    }
  
    if (!deletedReason.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please provide a reason for deletion",
        life: 3000,
      });
      return;
    }
  
    setIsDeleting(true);
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  
    const payload = {
      otp: otpInput.trim(),
      reason: deletedReason.trim()
    };
  
    EmailApi.deleteEmailTemplate(templateToDelete, payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response.data.message,
            life: 3000,
          });
          getAllEmailTemplates();
          handleCloseModal();
        }
      })
      .catch(error => {
        console.error("Error deleting template:", error);
        const errorMessage = error.response?.data?.message || "Failed to delete template";
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 3000,
        });
  
        if (errorMessage.toLowerCase().includes("invalid otp") || errorMessage.toLowerCase().includes("expired")) {
          setOtpInput("");
        }
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };
  
  const handleCloseModal = () => {
    setDeleteModal(false)
    setTemplateToDelete(null)
    setOtpInput("")
    setDeletedReason("")
    setIsOtpSent(false)
    setIsDeleting(false)
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <Row>
        <Col xs="12">
          <div className="template-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Email Templates"
              handleClick={handleBreadcrumbClick}
            />
              
            <div className="d-flex align-items-center gap-3">
              <div className="search-container">
                <Input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <i className="mdi mdi-magnify search-icon"></i>
              </div>
              
              {(userData.role === "admin" ||
                hasPermission(permissions, "emailTemplate", "POST")) && (
                <>
                  {selectedTemplates.length === 0 && (
                    <Button
                      type="button"
                      color="primary"
                      onClick={handleAddTemplateClick}
                    >
                      <i className="mdi mdi-plus me-1"></i>
                      Add Template
                    </Button>
                  )}

                  {selectedTemplates.length > 0 && (
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        onClick={() => handleUpdateStatus("approve")}
                      >
                        <i className="mdi mdi-check me-1"></i>
                        Approve ({selectedTemplates.length})
                      </Button>
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        onClick={() => handleUpdateStatus("reject")}
                      >
                        <i className="mdi mdi-close me-1"></i>
                        Reject ({selectedTemplates.length})
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
            {(userData.role === "admin" ||
              hasPermission(permissions, "emailTemplate", "GET")) && (
              <div className="data-table-container">
                <div className="table-responsive">
                {loading ? (
                  <DataTableLoader cols={10} />
                ) : (
                  <Table className="table mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            onClick={toggleSelectAll}
                            checked={
                              selectedTemplates.length ===
                              filteredTemplateDetails.length
                            }
                          />
                        </th>
                        <th>#</th>
                        <th>Template Name</th>
                        <th>Subject</th>
                        <th>
                          <span className="d-flex  align-items-center gap-2 justify-content-center">
                            <span>Verified Status</span>
                            <span
                              onClick={e => op.current.toggle(e)}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="mdi mdi-filter" />
                            </span>
                          </span>
                          <OverlayPanel ref={op}>
                            <Input
                              type="select"
                              value={filterStatus}
                              onChange={e => {
                                setFilterStatus(e.target.value)
                                op.current.hide()
                              }}
                              style={{ width: "150px" }}
                            >
                              <option value="all">All</option>
                              <option value="approve">Approved</option>
                              <option value="reject">Rejected</option>
                              <option value="pending">Pending</option>
                            </Input>
                          </OverlayPanel>
                        </th>
                        {(userData.role === "admin" ||
                          hasPermission(
                            permissions,
                            "emailTemplate",
                            "PUT",
                          )) && <th>Edit</th>}
                        {(userData.role === "admin" ||
                          hasPermission(
                            permissions,
                            "emailTemplate",
                            "DELETE",
                          )) && <th className="text-center">Delete</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTemplateDetails.map((data, index) => (
                        <tr key={data._id} className="template-table-row">
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedTemplates.includes(data._id)}
                              onClick={() => toggleSelection(data._id)}
                            />
                          </td>
                          <th scope="row">{(page - 1) * limit + index + 1}</th>
                          <td>{data.title}</td>
                          <td>{data.subject}</td>
                          <td className="text-center">
                            <StatusBadge 
                              status={data?.verifiedStatus === "approve" ? "Approved" : data?.verifiedStatus === "reject" ? "Rejected" : "Pending"}
                              type={data?.verifiedStatus === "approve" ? "active" : data?.verifiedStatus === "reject" ? "inactive" : "pending"}
                            />
                          </td>
                          {(userData.role === "admin" ||
                            hasPermission(
                              permissions,
                              "emailTemplate",
                              "PUT",
                            )) && (
                            <td>
                              <ActionIcon
                                type="edit"
                                onClick={() => {
                                  navigate(`/edit-email-template/${data._id}`, {
                                    state: data,
                                  })
                                }}
                                tooltip="Edit Template"
                              />
                            </td>
                          )}
                          {(userData.role === "admin" ||
                            hasPermission(
                              permissions,
                              "emailTemplate",
                              "DELETE",
                            )) && (
                            <td className="text-center">
                              <ActionIcon
                                type="delete"
                                onClick={() => handleDeleteTemplate(data._id)}
                                tooltip="Delete Template"
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    </Table>
                  )}
                </div>
              </div>
            )}
            <StandardPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Col>
        </Row>
      <Modal isOpen={deleteModal} toggle={handleCloseModal} centered size="lg">
        <ModalHeader toggle={handleCloseModal}>Delete Template</ModalHeader>
        <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
          <div className="text-center">
            <i
              className="ri-delete-bin-2-line"
              style={{ fontSize: "3rem", color: "#f46a6a" }}
            />
            <h4 className="mt-3">Are you sure?</h4>
            <p className="text-muted">
              Do you really want to delete this template? This action cannot be
              undone.
            </p>
            {!isOtpSent ? (
              <Button
                color="primary"
                onClick={generateOtp}
                className="mt-2"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Sending...
                  </>
                ) : (
                  "Generate OTP"
                )}
              </Button>
            ) : (
              <div className="mt-3">
                <p className="text-muted mb-2">
                  Please enter the OTP sent to admin email
                </p>
                <Input
                  type="text"
                  placeholder="Enter OTP"
                  value={otpInput}
                  onChange={handleOtpChange}
                  className="text-center otp-input mb-3"
                  maxLength="5"
                 
                  autoComplete="off"
                />

                <div className="mt-3">
                  <label className="form-label text-start d-block">
                    Reason for Deletion
                  </label>
                  <Input
                    type="textarea"
                    placeholder="Please provide a reason for deletion"
                    value={deletedReason}
                    onChange={e => setDeletedReason(e.target.value)}
                    className="reason-input"
                    rows="3"
                    required
                  />
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={handleCloseModal}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          {isOtpSent && (
            <Button
              color="danger"
              onClick={confirmDelete}
              disabled={isDeleting || !otpInput || !deletedReason.trim()}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  )
}

export default EmailTemplateDataTable
